import { Events } from './events';

type TimelineEvent = {
    id: string;
    time: number;
    label: string;
    description?: string;
};

type ParticipantKeyframe = {
    time: number;
    position: [number, number, number];
    heading: number;
};

type Participant = {
    id: string;
    name: string;
    meshRef?: string;
    color: [number, number, number];
    pathKeyframes: ParticipantKeyframe[];
};

type ReconstructionTimeline = {
    id: string;
    name: string;
    events: TimelineEvent[];
    participants: Participant[];
    duration: number;
    disclaimers: string[];
};

type ReconstructionTimelineScrubState = {
    time: number;
    duration: number;
    progress: number;
    eventId: string | null;
    eventIndex: number;
};

type ReconstructionTimelineParticipantSample = {
    id: string;
    name: string;
    color: [number, number, number];
    meshRef?: string;
    position: [number, number, number];
    heading: number;
    keyframeIndex: number;
    alpha: number;
};

type ReconstructionTimelineLeadMetricsUnits = {
    speed: string;
    distance: string;
    time: string;
};

type ReconstructionTimelineLeadMetrics = {
    available: boolean;
    requestedTime: number;
    time: number;
    deltaTime: number;
    speed: number;
    cumulativeDistance: number;
    participantId: string | null;
    participantName: string | null;
    label: string;
    units: ReconstructionTimelineLeadMetricsUnits;
    assumptions: string;
};

const isFiniteNumber = (value: unknown): value is number => {
    return typeof value === 'number' && Number.isFinite(value);
};

const sanitizeNonEmptyString = (value: unknown): string | null => {
    if (typeof value !== 'string') {
        return null;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
};

const sanitizeOptionalString = (value: unknown): string | undefined => {
    if (value === undefined) {
        return undefined;
    }
    return sanitizeNonEmptyString(value) ?? undefined;
};

const sanitizeVec3Tuple = (value: unknown): [number, number, number] | null => {
    if (!Array.isArray(value) || value.length !== 3) {
        return null;
    }

    const [x, y, z] = value;
    if (!isFiniteNumber(x) || !isFiniteNumber(y) || !isFiniteNumber(z)) {
        return null;
    }

    return [x, y, z];
};

const cloneEvent = (event: TimelineEvent): TimelineEvent => {
    return {
        ...event
    };
};

const cloneKeyframe = (keyframe: ParticipantKeyframe): ParticipantKeyframe => {
    return {
        ...keyframe,
        position: [keyframe.position[0], keyframe.position[1], keyframe.position[2]]
    };
};

const cloneParticipant = (participant: Participant): Participant => {
    return {
        ...participant,
        color: [participant.color[0], participant.color[1], participant.color[2]],
        pathKeyframes: participant.pathKeyframes.map(cloneKeyframe)
    };
};

const cloneTimeline = (timeline: ReconstructionTimeline): ReconstructionTimeline => {
    return {
        ...timeline,
        events: timeline.events.map(cloneEvent),
        participants: timeline.participants.map(cloneParticipant),
        disclaimers: timeline.disclaimers.slice()
    };
};

const sanitizeTimelineEvent = (value: unknown, duration: number): TimelineEvent | null => {
    if (!value || typeof value !== 'object') {
        return null;
    }

    const candidate = value as Record<string, unknown>;
    const id = sanitizeNonEmptyString(candidate.id);
    const label = sanitizeNonEmptyString(candidate.label);
    const description = sanitizeOptionalString(candidate.description);

    if (!id || !label || !isFiniteNumber(candidate.time)) {
        return null;
    }

    const time = candidate.time;
    if (time < 0 || time > duration) {
        return null;
    }

    return {
        id,
        time,
        label,
        description
    };
};

const sanitizeParticipantKeyframes = (value: unknown, duration: number): ParticipantKeyframe[] | null => {
    if (!Array.isArray(value)) {
        return null;
    }

    const sanitized: ParticipantKeyframe[] = [];

    for (const item of value) {
        if (!item || typeof item !== 'object') {
            return null;
        }

        const candidate = item as Record<string, unknown>;
        const position = sanitizeVec3Tuple(candidate.position);
        if (!position || !isFiniteNumber(candidate.time) || !isFiniteNumber(candidate.heading)) {
            return null;
        }

        const time = candidate.time;
        if (time < 0 || time > duration) {
            return null;
        }

        const previous = sanitized[sanitized.length - 1];
        if (previous && time <= previous.time) {
            return null;
        }

        sanitized.push({
            time,
            position,
            heading: candidate.heading
        });
    }

    return sanitized;
};

const sanitizeParticipant = (value: unknown, duration: number): Participant | null => {
    if (!value || typeof value !== 'object') {
        return null;
    }

    const candidate = value as Record<string, unknown>;
    const id = sanitizeNonEmptyString(candidate.id);
    const name = sanitizeNonEmptyString(candidate.name);
    const meshRef = sanitizeOptionalString(candidate.meshRef);
    const color = sanitizeVec3Tuple(candidate.color);
    const pathKeyframes = sanitizeParticipantKeyframes(candidate.pathKeyframes, duration);

    if (!id || !name || !color || !pathKeyframes) {
        return null;
    }

    return {
        id,
        name,
        meshRef,
        color,
        pathKeyframes
    };
};

const sanitizeDisclaimers = (value: unknown): string[] | null => {
    if (!Array.isArray(value)) {
        return null;
    }

    const sanitized: string[] = [];
    for (const item of value) {
        const disclaimer = sanitizeNonEmptyString(item);
        if (!disclaimer) {
            return null;
        }
        sanitized.push(disclaimer);
    }

    return sanitized;
};

const sanitizeReconstructionTimeline = (value: unknown): ReconstructionTimeline | null => {
    if (!value || typeof value !== 'object') {
        return null;
    }

    const candidate = value as Record<string, unknown>;
    const id = sanitizeNonEmptyString(candidate.id);
    const name = sanitizeNonEmptyString(candidate.name);
    const disclaimers = sanitizeDisclaimers(candidate.disclaimers);

    if (!id || !name || !disclaimers || !Array.isArray(candidate.events) || !Array.isArray(candidate.participants) || !isFiniteNumber(candidate.duration)) {
        return null;
    }

    const duration = candidate.duration;
    if (duration < 0) {
        return null;
    }

    const events = candidate.events
    .map(event => sanitizeTimelineEvent(event, duration))
    .filter((event): event is TimelineEvent => !!event);

    if (events.length !== candidate.events.length) {
        return null;
    }

    const participants = candidate.participants
    .map(participant => sanitizeParticipant(participant, duration))
    .filter((participant): participant is Participant => !!participant);

    if (participants.length !== candidate.participants.length) {
        return null;
    }

    const eventIds = new Set(events.map(event => event.id));
    const participantIds = new Set(participants.map(participant => participant.id));

    if (eventIds.size !== events.length || participantIds.size !== participants.length) {
        return null;
    }

    const orderedEvents = events.slice().sort((a, b) => a.time - b.time);

    return {
        id,
        name,
        events: orderedEvents,
        participants,
        duration,
        disclaimers
    };
};

const mergeParticipantPatch = (participant: Participant, patch: Record<string, unknown>, duration: number): Participant | null => {
    const merged = {
        ...participant,
        ...patch
    };

    if (Object.prototype.hasOwnProperty.call(patch, 'meshRef')) {
        merged.meshRef = patch.meshRef as string | undefined;
    }

    return sanitizeParticipant(merged, duration);
};

const mergeEventPatch = (event: TimelineEvent, patch: Record<string, unknown>, duration: number): TimelineEvent | null => {
    const merged = {
        ...event,
        ...patch
    };

    if (Object.prototype.hasOwnProperty.call(patch, 'description')) {
        merged.description = patch.description as string | undefined;
    }

    return sanitizeTimelineEvent(merged, duration);
};

const scrubTimesEqual = (a: number, b: number) => {
    return Math.abs(a - b) <= 1e-6;
};

const clampTime = (value: number, duration: number) => {
    return Math.max(0, Math.min(duration, value));
};

const lerp = (a: number, b: number, alpha: number) => {
    return a + (b - a) * alpha;
};

const distanceBetween = (from: [number, number, number], to: [number, number, number]) => {
    const dx = to[0] - from[0];
    const dy = to[1] - from[1];
    const dz = to[2] - from[2];
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
};

const interpolateHeading = (fromHeading: number, toHeading: number, alpha: number) => {
    const wrappedDelta = ((toHeading - fromHeading + 540) % 360) - 180;
    return fromHeading + wrappedDelta * alpha;
};

const sampleParticipantAtTime = (participant: Participant, time: number): ReconstructionTimelineParticipantSample | null => {
    const keyframes = participant.pathKeyframes;
    if (keyframes.length === 0) {
        return null;
    }

    if (keyframes.length === 1 || time <= keyframes[0].time) {
        return {
            id: participant.id,
            name: participant.name,
            color: [participant.color[0], participant.color[1], participant.color[2]],
            meshRef: participant.meshRef,
            position: [keyframes[0].position[0], keyframes[0].position[1], keyframes[0].position[2]],
            heading: keyframes[0].heading,
            keyframeIndex: 0,
            alpha: 0
        };
    }

    const lastIndex = keyframes.length - 1;
    if (time >= keyframes[lastIndex].time) {
        return {
            id: participant.id,
            name: participant.name,
            color: [participant.color[0], participant.color[1], participant.color[2]],
            meshRef: participant.meshRef,
            position: [keyframes[lastIndex].position[0], keyframes[lastIndex].position[1], keyframes[lastIndex].position[2]],
            heading: keyframes[lastIndex].heading,
            keyframeIndex: lastIndex,
            alpha: 1
        };
    }

    for (let i = 0; i < keyframes.length - 1; i++) {
        const from = keyframes[i];
        const to = keyframes[i + 1];

        if (time < from.time || time > to.time) {
            continue;
        }

        const interval = to.time - from.time;
        const alpha = interval > 0 ? Math.max(0, Math.min(1, (time - from.time) / interval)) : 0;

        return {
            id: participant.id,
            name: participant.name,
            color: [participant.color[0], participant.color[1], participant.color[2]],
            meshRef: participant.meshRef,
            position: [
                lerp(from.position[0], to.position[0], alpha),
                lerp(from.position[1], to.position[1], alpha),
                lerp(from.position[2], to.position[2], alpha)
            ],
            heading: interpolateHeading(from.heading, to.heading, alpha),
            keyframeIndex: i,
            alpha
        };
    }

    return null;
};

const sampleParticipantsAtTime = (timeline: ReconstructionTimeline, time: number) => {
    const clampedTime = clampTime(time, timeline.duration);
    return timeline.participants
    .map(participant => sampleParticipantAtTime(participant, clampedTime))
    .filter((sample): sample is ReconstructionTimelineParticipantSample => !!sample);
};

const createEmptyLeadMetrics = (requestedTime: number, clampedTime: number): ReconstructionTimelineLeadMetrics => {
    return {
        available: false,
        requestedTime,
        time: clampedTime,
        deltaTime: 0,
        speed: 0,
        cumulativeDistance: 0,
        participantId: null,
        participantName: null,
        label: 'Lead participant: unavailable',
        units: {
            speed: 'world-units/s',
            distance: 'world-units',
            time: 's'
        },
        assumptions: 'Lead = first participant in timeline order.'
    };
};

const getLeadMetricsAtTime = (timeline: ReconstructionTimeline | undefined, requestedTime: number): ReconstructionTimelineLeadMetrics => {
    if (!timeline) {
        return createEmptyLeadMetrics(requestedTime, 0);
    }

    const clampedTime = clampTime(requestedTime, timeline.duration);
    const leadParticipant = timeline.participants[0];

    if (!leadParticipant || leadParticipant.pathKeyframes.length < 2) {
        return {
            ...createEmptyLeadMetrics(requestedTime, clampedTime),
            label: leadParticipant ?
                `Lead participant: ${leadParticipant.name} (insufficient keyframes)` :
                'Lead participant: unavailable'
        };
    }

    const keyframes = leadParticipant.pathKeyframes;
    let segmentIndex = 0;

    if (clampedTime >= keyframes[keyframes.length - 1].time) {
        segmentIndex = keyframes.length - 2;
    } else {
        for (let i = 0; i < keyframes.length - 1; i++) {
            if (clampedTime <= keyframes[i + 1].time) {
                segmentIndex = i;
                break;
            }
        }
    }

    const from = keyframes[segmentIndex];
    const to = keyframes[segmentIndex + 1];
    const segmentDt = Math.max(0, to.time - from.time);
    const segmentDistance = distanceBetween(from.position, to.position);

    const alpha = segmentDt > 0 ?
        Math.max(0, Math.min(1, (clampedTime - from.time) / segmentDt)) :
        0;

    let cumulativeDistance = 0;
    for (let i = 0; i < segmentIndex; i++) {
        cumulativeDistance += distanceBetween(keyframes[i].position, keyframes[i + 1].position);
    }
    cumulativeDistance += segmentDistance * alpha;

    const firstTime = keyframes[0].time;
    const deltaTime = Math.max(0, clampedTime - firstTime);
    const speed = clampedTime < firstTime ?
        0 :
        (segmentDt > 0 ? (segmentDistance / segmentDt) : 0);

    return {
        available: true,
        requestedTime,
        time: clampedTime,
        deltaTime,
        speed,
        cumulativeDistance,
        participantId: leadParticipant.id,
        participantName: leadParticipant.name,
        label: `Lead participant: ${leadParticipant.name} (first in timeline order)`,
        units: {
            speed: 'world-units/s',
            distance: 'world-units',
            time: 's'
        },
        assumptions: 'Lead = first participant in timeline order. Speed = current segment distance ÷ segment Δt (0 before first keyframe). Distance = completed segments + interpolated segment progress.'
    };
};

const registerReconstructionTimelineEvents = (events: Events) => {
    let timeline: ReconstructionTimeline | undefined;
    let scrubTime = 0;

    const clampScrubTime = (value: number, duration: number) => {
        return clampTime(value, duration);
    };

    const getScrubState = (): ReconstructionTimelineScrubState | undefined => {
        if (!timeline) {
            return undefined;
        }

        const duration = timeline.duration;
        const time = clampScrubTime(scrubTime, duration);
        let eventIndex = -1;

        for (let i = 0; i < timeline.events.length; i++) {
            if (timeline.events[i].time <= time) {
                eventIndex = i;
            } else {
                break;
            }
        }

        return {
            time,
            duration,
            progress: duration > 0 ? (time / duration) : 0,
            eventId: eventIndex >= 0 ? timeline.events[eventIndex].id : null,
            eventIndex
        };
    };

    const fireScrubChanged = () => {
        const state = getScrubState();
        events.fire('reconstructionTimeline.scrubChanged', state);
        events.fire('reconstructionTimeline.scrubTime', state?.time ?? 0);
    };

    const setScrubTime = (next: unknown) => {
        if (!timeline || !isFiniteNumber(next)) {
            return false;
        }

        const clamped = clampScrubTime(next, timeline.duration);
        if (scrubTimesEqual(clamped, scrubTime)) {
            return true;
        }

        scrubTime = clamped;
        fireScrubChanged();
        return true;
    };

    const fireChanged = () => {
        events.fire('reconstructionTimeline.changed', timeline ? cloneTimeline(timeline) : undefined);
        fireScrubChanged();
    };

    const setTimeline = (next: unknown): boolean => {
        if (next === undefined || next === null) {
            timeline = undefined;
            scrubTime = 0;
            fireChanged();
            return true;
        }

        const sanitized = sanitizeReconstructionTimeline(next);
        if (!sanitized) {
            return false;
        }

        timeline = cloneTimeline(sanitized);
        scrubTime = clampScrubTime(scrubTime, timeline.duration);
        fireChanged();
        return true;
    };

    const list = () => {
        return timeline ? cloneTimeline(timeline) : undefined;
    };

    events.function('reconstructionTimeline.get', () => list());
    events.function('reconstructionTimeline.validate', (value: unknown) => !!sanitizeReconstructionTimeline(value));
    events.function('reconstructionTimeline.set', (value: unknown) => setTimeline(value));
    events.function('reconstructionTimeline.scrubState', () => getScrubState());
    events.function('reconstructionTimeline.scrubTime', () => {
        return timeline ? clampScrubTime(scrubTime, timeline.duration) : 0;
    });
    events.function('reconstructionTimeline.sampleParticipants', (requestedTime?: unknown) => {
        if (!timeline) {
            return [] as ReconstructionTimelineParticipantSample[];
        }

        const time = isFiniteNumber(requestedTime) ?
            requestedTime : clampScrubTime(scrubTime, timeline.duration);

        return sampleParticipantsAtTime(timeline, time);
    });
    events.function('reconstructionTimeline.leadMetrics', (requestedTime?: unknown) => {
        if (!timeline) {
            const requested = isFiniteNumber(requestedTime) ? requestedTime : 0;
            return getLeadMetricsAtTime(undefined, requested);
        }

        const time = isFiniteNumber(requestedTime) ?
            requestedTime :
            clampScrubTime(scrubTime, timeline.duration);

        return getLeadMetricsAtTime(timeline, time);
    });
    events.function('reconstructionTimeline.setScrubTime', (value: unknown) => {
        return setScrubTime(value);
    });

    events.on('reconstructionTimeline.set', (value: unknown) => {
        setTimeline(value);
    });

    events.on('reconstructionTimeline.setAll', (value: unknown) => {
        setTimeline(value);
    });

    events.on('reconstructionTimeline.setScrubTime', (value: unknown) => {
        setScrubTime(value);
    });

    events.on('reconstructionTimeline.clear', () => {
        setTimeline(undefined);
    });

    events.on('reconstructionTimeline.addEvent', (eventValue: unknown) => {
        if (!timeline) {
            return;
        }

        const event = sanitizeTimelineEvent(eventValue, timeline.duration);
        if (!event || timeline.events.some(existing => existing.id === event.id)) {
            return;
        }

        timeline = {
            ...timeline,
            events: timeline.events.concat(event).sort((a, b) => a.time - b.time)
        };
        fireChanged();
    });

    events.on('reconstructionTimeline.updateEvent', (payload: { id: string; patch: Record<string, unknown> }) => {
        if (!timeline || typeof payload?.id !== 'string' || !payload.patch || typeof payload.patch !== 'object') {
            return;
        }

        let changed = false;
        const nextEvents = timeline.events.map((item) => {
            if (item.id !== payload.id) {
                return item;
            }

            const merged = mergeEventPatch(item, payload.patch, timeline.duration);
            if (!merged) {
                return item;
            }

            if (merged.id !== item.id && timeline.events.some(other => other.id === merged.id)) {
                return item;
            }

            changed = true;
            return merged;
        });

        if (!changed) {
            return;
        }

        timeline = {
            ...timeline,
            events: nextEvents.sort((a, b) => a.time - b.time)
        };
        fireChanged();
    });

    events.on('reconstructionTimeline.removeEvent', (id: string) => {
        if (!timeline || typeof id !== 'string') {
            return;
        }

        const next = timeline.events.filter(item => item.id !== id);
        if (next.length === timeline.events.length) {
            return;
        }

        timeline = {
            ...timeline,
            events: next
        };
        scrubTime = clampScrubTime(scrubTime, timeline.duration);
        fireChanged();
    });

    events.on('reconstructionTimeline.addParticipant', (participantValue: unknown) => {
        if (!timeline) {
            return;
        }

        const participant = sanitizeParticipant(participantValue, timeline.duration);
        if (!participant || timeline.participants.some(existing => existing.id === participant.id)) {
            return;
        }

        timeline = {
            ...timeline,
            participants: timeline.participants.concat(participant)
        };
        fireChanged();
    });

    events.on('reconstructionTimeline.updateParticipant', (payload: { id: string; patch: Record<string, unknown> }) => {
        if (!timeline || typeof payload?.id !== 'string' || !payload.patch || typeof payload.patch !== 'object') {
            return;
        }

        let changed = false;
        const nextParticipants = timeline.participants.map((item) => {
            if (item.id !== payload.id) {
                return item;
            }

            const merged = mergeParticipantPatch(item, payload.patch, timeline.duration);
            if (!merged) {
                return item;
            }

            if (merged.id !== item.id && timeline.participants.some(other => other.id === merged.id)) {
                return item;
            }

            changed = true;
            return merged;
        });

        if (!changed) {
            return;
        }

        timeline = {
            ...timeline,
            participants: nextParticipants
        };
        fireChanged();
    });

    events.on('reconstructionTimeline.removeParticipant', (id: string) => {
        if (!timeline || typeof id !== 'string') {
            return;
        }

        const next = timeline.participants.filter(item => item.id !== id);
        if (next.length === timeline.participants.length) {
            return;
        }

        timeline = {
            ...timeline,
            participants: next
        };
        fireChanged();
    });

    events.on('reconstructionTimeline.setDisclaimers', (value: unknown) => {
        if (!timeline) {
            return;
        }

        const disclaimers = sanitizeDisclaimers(value);
        if (!disclaimers) {
            return;
        }

        timeline = {
            ...timeline,
            disclaimers
        };
        fireChanged();
    });
};

export { registerReconstructionTimelineEvents };
export type {
    ReconstructionTimeline,
    TimelineEvent,
    Participant,
    ParticipantKeyframe,
    ReconstructionTimelineScrubState,
    ReconstructionTimelineParticipantSample,
    ReconstructionTimelineLeadMetrics,
    ReconstructionTimelineLeadMetricsUnits
};
